// --- React & Redux
import React, { useEffect, useRef, useState } from "react";
import { useAsyncEffect } from "tap/utils/hooks/useAsyncEffect";
import { useLoading } from "tap/utils/hooks/useLoading";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { debounce } from "lodash";
// --- External UI Libraries
import { Col, Row } from "antd";
import Button from "@dtci/spellbook/Button";
import Select from "@dtci/spellbook/Select";
import { MultiSelect } from "@dtci/spellbook/MultiSelect";
// --- Components
import { InputLabel } from "tap/components/InputLabel";
import { ClearableFilterInput } from "tap/components/ClearableFilterInput";
// --- Actions & Hooks
import { usePMXPricesAndDatesData } from "../../hooks/usePMXPricesAndDatesData";
import { usePmxPricesAndDatesActions } from "tap/store/pmxPricesAndDates/usePmxPricesAndDates.js";
// --- Utilities
import { tapApi } from "tap/api.js";
import { parseSearchParams, updateSearchParams } from "tap/utils/urlParams.js";
import { getItemRange } from "tap/utils/pagination.js";
import {
  buildFilterFromParams,
  cleanPMXFilter,
  mapPmxDistinctOptions,
} from "./utils/pmxPricesAndDatesSearchUtils";
import { validateSpecialCharacters } from "tap/utils/validateSpecialCharacters.js";
// --- Styles
import "./styles.scss";

export function PMXPricesAndDatesSearchForm() {
  const location = useLocation();
  const navigate = useNavigate();
  const isInitialMount = useRef(true);
  const { setLoadingTrue, setLoadingFalse } = useLoading();

  const {
    fetchPMXPricesAndDates,
    updatePMXPricesAndDates,
    clearPMXPricesAndDates,
    pmxPricesAndDatesRange,
    pmxPricesAndDatesSort,
    itemsPerPage,
  } = usePMXPricesAndDatesData();

  const { updatePMXPricesAndDatesSearchForm } = usePmxPricesAndDatesActions();

  const {
    filterForm,
    rlHistoricalRecordOptions,
    productTypeOptions,
    productConfigOptions,
    territoryOptions,
    productPlatformOptions,
  } = useSelector((s) => s.pmxPricesAndDates);

  const [validationErrors, setValidationErrors] = useState({
    catalogId: undefined,
    catalogName: undefined,
  });
  const debounceTimerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // Load dropdown options on mount
  useAsyncEffect(async ({ isMounted }) => {
    try {
      const [territoryRes, platformRes] = await Promise.all([
        tapApi.territories(),
        tapApi.productPlatforms(),
      ]);

      if (isMounted()) {
        const territories = mapPmxDistinctOptions(territoryRes);
        const productPlatforms = mapPmxDistinctOptions(platformRes);
        updatePMXPricesAndDates({
          territoryOptions: territories,
          productPlatformOptions: productPlatforms,
        });
      }
    } catch (error) {
      console.error("Failed to load initial options:", error);
      if (isMounted()) {
        updatePMXPricesAndDates({
          territoryOptions: [],
          productPlatformOptions: [],
        });
      }
    }
  }, []);

  // Fetch data on every navigation to this page
  useEffect(() => {
    const params = parseSearchParams(location.search);

    // Build filter data from URL params using helper
    const filterData = buildFilterFromParams(params, {
      rlHistoricalRecordOptions,
      productTypeOptions,
      territoryOptions,
      productPlatformOptions,
      productConfigOptions,
    });

    let rangeOverride;
    if (filterData.pageSize) {
      rangeOverride = getItemRange(0, filterData.pageSize);
      updatePMXPricesAndDates({ itemsPerPage: filterData.pageSize });
    }

    const hasUrlParams = Object.keys(filterData).length > 0;

    // Update form state from URL params
    if (hasUrlParams) {
      updatePMXPricesAndDatesSearchForm({
        key: "filterForm",
        data: filterData,
      });
    }

    // Merge URL-derived filters onto Redux filterForm so defaults apply when the URL
    // only carries partial params (e.g. pageSize).
    const filterForFetch =
      hasUrlParams ? { ...filterForm, ...filterData } : filterForm;

    fetchData(filterForFetch, rangeOverride);
    isInitialMount.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  // When options load, re-parse URL and fetch if needed
  useEffect(() => {
    if (isInitialMount.current) return;

    const params = parseSearchParams(location.search);
    const hasMultiSelectParams =
      params.territory || params.productPlatform || params.productConfig;

    if (
      hasMultiSelectParams &&
      (territoryOptions.length > 0 || productPlatformOptions.length > 0)
    ) {
      const filterData = buildFilterFromParams(params, {
        rlHistoricalRecordOptions,
        productTypeOptions,
        territoryOptions,
        productPlatformOptions,
        productConfigOptions,
      });

      const filterForFetch = { ...filterForm, ...filterData };

      updatePMXPricesAndDatesSearchForm({
        key: "filterForm",
        data: filterData,
      });
      fetchData(filterForFetch);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [territoryOptions, productPlatformOptions]);

  // Fetch data helper
  const fetchData = async (filterData, rangeOverride) => {
    setLoadingTrue();
    const range = rangeOverride ?? getItemRange(0, itemsPerPage);
    const data = await fetchPMXPricesAndDates(range, filterData);

    if (data) {
      updatePMXPricesAndDates({
        pricesAndDates: data?.content,
        totalPages: data?.totalPages,
        totalItems: data?.totalElements,
        currentPage: data?.pageable?.pageNumber ?? 0,
        pmxPricesAndDatesRange: range,
      });
    }
    setLoadingFalse();
  };

  // Reset filters - only navigate, let useEffect handle the fetch
  const handleResetFilter = () => {
    navigate(location.pathname);
    clearPMXPricesAndDates();
    setValidationErrors({
      catalogId: undefined,
      catalogName: undefined,
    });
  };

  const handleInputChange = (field, value) => {
    let newValidationErrors = validationErrors;
    if (field === "catalogId") {
      const error = validateSpecialCharacters(value);
      newValidationErrors = { ...validationErrors, [field]: error };
      setValidationErrors(newValidationErrors);
    }

    const newFilterForm = { ...filterForm, [field]: value };
    updatePMXPricesAndDatesSearchForm({
      key: "filterForm",
      data: { [field]: value },
    });

    // Debounced search
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      const hasErrors = Object.values(newValidationErrors).some(
        (error) => error,
      );
      if (!hasErrors) {
        const filtered = cleanPMXFilter(
          newFilterForm,
          getItemRange(0, itemsPerPage),
          pmxPricesAndDatesSort,
        );
        updateSearchParams(location, navigate, filtered);
      }
    }, 400);
  };

  const handleClearField = (field) => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }

    const newValidationErrors = { ...validationErrors, [field]: undefined };
    setValidationErrors(newValidationErrors);

    const newFilterForm = { ...filterForm, [field]: "" };
    updatePMXPricesAndDatesSearchForm({
      key: "filterForm",
      data: { [field]: "" },
    });

    const resetRange = getItemRange(0, itemsPerPage);
    const filtered = cleanPMXFilter(
      newFilterForm,
      resetRange,
      pmxPricesAndDatesSort,
    );
    updateSearchParams(location, navigate, filtered);
    fetchData(newFilterForm, resetRange);
  };

  return (
    <Row className="prices-and-dates-filter-form">
      <Col>
        <InputLabel colProps={{ span: 24 }} label="RL Historical Record">
          <Select
            options={rlHistoricalRecordOptions}
            isClearable
            onChange={(value) => handleInputChange("isHistoricalRecord", value)}
            value={filterForm?.isHistoricalRecord || ""}
          />
        </InputLabel>
      </Col>
      <Col>
        <InputLabel colProps={{ span: 24 }} label="Catalog ID">
          <ClearableFilterInput
            value={filterForm?.catalogId || ""}
            onChange={(e) => handleInputChange("catalogId", e.target.value)}
            onClear={() => handleClearField("catalogId")}
            width="auto"
            id="catalogId"
            name="catalogId"
            placeholder="Enter"
            className={validationErrors.catalogId ? "input-error" : ""}
          />
          {validationErrors.catalogId && (
            <div className="validation-error">{validationErrors.catalogId}</div>
          )}
        </InputLabel>
      </Col>
      <Col>
        <InputLabel colProps={{ span: 24 }} label="Catalog Name">
          <ClearableFilterInput
            value={filterForm?.catalogName || ""}
            onChange={(e) => handleInputChange("catalogName", e.target.value)}
            onClear={() => handleClearField("catalogName")}
            width="auto"
            id="catalogName"
            name="catalogName"
            placeholder="Enter"
            className={validationErrors.catalogName ? "input-error" : ""}
          />
          {validationErrors.catalogName && (
            <div className="validation-error">
              {validationErrors.catalogName}
            </div>
          )}
        </InputLabel>
      </Col>
      <Col>
        <InputLabel colProps={{ span: 24 }} label="Product Type">
          <Select
            options={productTypeOptions}
            isClearable
            onChange={(value) => handleInputChange("productType", value)}
            value={filterForm?.productType || ""}
          />
        </InputLabel>
      </Col>
      <Col>
        <InputLabel colProps={{ span: 24 }} label="Territory">
          <MultiSelect
            onChange={(value) => handleInputChange("territory", value)}
            name="territory"
            options={territoryOptions}
            isSearchable
            isClearable
            value={filterForm.territory || ""}
          />
        </InputLabel>
      </Col>
      <Col>
        <InputLabel colProps={{ span: 24 }} label="Product Platform">
          <MultiSelect
            onChange={(value) => handleInputChange("productPlatform", value)}
            name="productPlatform"
            options={productPlatformOptions}
            isSearchable
            isClearable
            value={filterForm.productPlatform || ""}
          />
        </InputLabel>
      </Col>
      <Col>
        <InputLabel colProps={{ span: 24 }} label="Product Config">
          <MultiSelect
            onChange={(value) => handleInputChange("productConfig", value)}
            name="productConfig"
            options={productConfigOptions}
            isSearchable
            isClearable
            value={filterForm.productConfig || ""}
          />
        </InputLabel>
      </Col>
      <Col>
        <InputLabel colProps={{ span: 24 }} label="PMX Release ID">
          <ClearableFilterInput
            value={filterForm?.pmxReleaseId || ""}
            onChange={(e) =>
              handleInputChange("pmxReleaseId", e.target.value)
            }
            onClear={() => handleClearField("pmxReleaseId")}
            width="auto"
            id="pmxReleaseId"
            name="pmxReleaseId"
            placeholder="Enter"
          />
        </InputLabel>
      </Col>
      <Col>
        <Button
          className="reset-button"
          sizeType="small"
          styleType="standard"
          onClick={handleResetFilter}
        >
          Reset
        </Button>
      </Col>
    </Row>
  );
}
